import { useEffect, useState } from 'react';

import { addBreadcrumb } from '@/integrations/sentry/errorReporting';

const SENTRY_CATEGORY_LOCAL_STORAGE = 'localStorage';
const SENTRY_LOCAL_STOREAGE_BREADCRUMB_LEVEL = 'info';

function getPreferences(localStorageKey: string): any | undefined {
  const localStorageValue = localStorage.getItem(localStorageKey);
  addBreadcrumb('GET localStorage', {
    category: SENTRY_CATEGORY_LOCAL_STORAGE,
    level: SENTRY_LOCAL_STOREAGE_BREADCRUMB_LEVEL,
    data: {
      hasValue: localStorageValue !== null,
      valueType: typeof localStorageValue,
    },
  });
  return localStorageValue;
}

function setPreferences(localStorageKey: string, localStorageValue: any) {
  addBreadcrumb('SET localStorage', {
    category: SENTRY_CATEGORY_LOCAL_STORAGE,
    level: SENTRY_LOCAL_STOREAGE_BREADCRUMB_LEVEL,
    data: {
      hasValue: localStorageValue !== undefined,
      valueType: typeof localStorageValue,
    },
  });
  localStorage.setItem(localStorageKey, localStorageValue);
}

function removePreferences(localStorageKey: string): any | undefined {
  addBreadcrumb('REMOVE localStorage', {
    category: SENTRY_CATEGORY_LOCAL_STORAGE,
    level: SENTRY_LOCAL_STOREAGE_BREADCRUMB_LEVEL,
  });
  return localStorage.removeItem(localStorageKey);
}

export { getPreferences, setPreferences, removePreferences };

type PreferencesNormalizer<TStored, TValue> = (value: TStored) => TValue;

export function usePreferencesState<TStored, TValue = TStored>(
  preferencesKey: string,
  defaultValue: TValue,
  normalize?: PreferencesNormalizer<TStored, TValue>
) {
  const readLocalStorage = (): TValue => {
    const preferencesValue = getPreferences(preferencesKey);
    try {
      if (!preferencesValue) return defaultValue;

      const storedValue = JSON.parse(preferencesValue) as TStored;
      return normalize ? normalize(storedValue) : (storedValue as unknown as TValue);
    } catch (_e) {
      removePreferences(preferencesKey);
      return defaultValue;
    }
  };

  const [state, setState] = useState<TValue>(() => readLocalStorage());

  useEffect(() => {
    setState(readLocalStorage());
  }, [preferencesKey]);

  useEffect(() => {
    if (state === undefined) {
      removePreferences(preferencesKey);
      return;
    }
    const preferencesAsString = JSON.stringify(state);
    setPreferences(preferencesKey, preferencesAsString);
  }, [preferencesKey, state]);

  return [state, setState] as const;
}
